import { Toaster, toast } from "sonner";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { fetchAccounts } from "./api/api";
import { useTheme } from "./context/ThemeContext";
import { useTrade } from "./context/TradeProvider";
import { useWatchlistData } from "./context/WatchlistContext";
import PriceFeedProvider from "./context/PriceFeedContext";
import Sidebar from "./components/Sidebar";
import VideoBackground from "./components/VideoBackground";
import NotificationsBadge from "./components/notifications/NotificationsBadge";
import NotificationsDrawer from "./components/notifications/NotificationsDrawer";
import { Sparkles } from "lucide-react";
import Dashboard from "./components/Dashboard";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminImpersonationLogin from "./pages/AdminImpersonationLogin";
import AgentChat from "./components/AgentChat";
import LandingPage from "./pages/LandingPage";
import Accounts from "./pages/Accounts";
import AccountsHubPreviewPage from "./pages/AccountsHubPreviewPage";
import WatchlistModal from "./components/WatchlistModal";
import SelectCTraderAccount from "./pages/SelectCTraderAccount";
import WinRateCalculatorPage from "./components/WinRateCalculator/WinRateCalculatorPage";
import AccountTradeListPage from "./pages/AccountTradeListPage";
import TradeHistoryViewerPage from "./pages/TradeHistoryViewerPage";
import SelectAccountForHistoryPage from "./pages/SelectAccountForHistoryPage";
import SettingsPage from "./pages/SettingsPage";
import ThemeSettingsPage from "./pages/ThemeSettingsPage";
import NotificationSettingsPage from "./pages/NotificationSettingsPage";
import ChartSnapshotConfigsPage from "./pages/ChartSnapshotConfigsPage";
import HistoricalAnalysisPage from "./pages/HistoricalAnalysisPage";
import AnalysisJobDetailsPage from "./pages/AnalysisJobDetailsPage";
import BotsListPage from "./pages/bots/BotsListPage";
import CreateBotPage from "./pages/bots/CreateBotPage";
import BotDetailPage from "./pages/bots/BotDetailPage";
import CreateBotVersionPage from "./pages/bots/CreateBotVersionPage";
import BotVersionDetailPage from "./pages/bots/BotVersionDetailPage";
import CreateEditBacktestConfigPage from "./pages/bots/CreateEditBacktestConfigPage";
import BacktestRunDetailPage from "./pages/bots/BacktestRunDetailPage";
import StrategyBuilderPage from "./pages/bots/StrategyBuilderPage";
import StrategyBuilderV2Page from "./pages/bots/StrategyBuilderV2Page";
import LiveRunsPage from "./pages/bots/LiveRunsPage";
import { IndicatorMetadataProvider } from "./context/IndicatorMetadataContext";
import InstallAppBanner from "./components/InstallAppBanner";
import { StrategySchemaProvider } from "./context/StrategySchemaContext";
import LiveTradePage from "./pages/LiveTradePage";
import NewStudioPage from "./pages/CustomStudioPage";
import GridTestPage from "./pages/GridTestPage";
import ChartTestPage from "./pages/ChartTestPage";
import MonitoringPage from "./pages/MonitoringPage";
import EconomicCalendarPage from "./pages/fundamentals/EconomicCalendarPage";
import NewsPage from "./pages/fundamentals/NewsPage";
import COTReportPage from "./pages/fundamentals/COTReportPage";
import EconomicIndicatorsPage from "./pages/fundamentals/EconomicIndicatorsPage";
import OptionsPage from "./pages/fundamentals/OptionsPage";
import KnowledgeGraphPage from "./pages/fundamentals/KnowledgeGraphPage";
import InsiderTradingPage from "./pages/fundamentals/InsiderTradingPage";
import BotVersionsPage from "./components/bots/BotVersionsPage";
import UserProfilePage from "./pages/UserProfilePage";
import PortfolioPage from "./pages/PortfolioPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import BillingPage from "./pages/BillingPage";
import OnboardingPage from "./pages/OnboardingPage";
import CTraderOnboardingModal from "./components/CTraderOnboardingModal";
import TrialBanner from "./components/subscription/TrialBanner";
import UpgradeFeatureGateModal from "./components/subscription/UpgradeFeatureGateModal";
import { listAccounts as listCTraderAccounts, onboardComplete as ctraderOnboardComplete, getCallbackAllowedOrigins } from "./api/ctraderApi";
import CTraderPopupCallback from "./pages/CTraderPopupCallback";
import AlertsListPage from "./pages/alerts/AlertsListPage";
import AlertDetailPage from "./pages/alerts/AlertDetailPage";
import { NotificationsProvider } from './context/NotificationsContext.jsx';
import AIRoutes from './ai-platform/AIRoutes';
import PendingTradesPage from './pages/PendingTradesPage';
import TradeApprovalDetailPage from './pages/TradeApprovalDetailPage';
import InvestorPitchPage from './pages/InvestorPitchPage';
import PitchDeckAdminPage from './pages/PitchDeckAdminPage';
import UnitEconomicsAdminPage from './pages/UnitEconomicsAdminPage';
import ScreenerPage from './pages/data-warehouse/ScreenerPage';
import ExplorerPage from './pages/data-warehouse/ExplorerPage';
import MarketExplorerPage from './pages/data-warehouse/MarketExplorerPage';
import ResearchHubPage from './pages/ResearchHubPage';
import TutorialPage from './pages/TutorialPage';
import CopyTradingPage from './pages/CopyTradingPage';
import AccessRouteGate from './access/AccessRouteGate';
import { APP_ROUTE_SURFACES } from './access/surfaces';

const FIRST_ACCOUNT_SKIP_KEY = "valpha:first-account-onboarding-skipped";

// Wrapper to extract :botId and render BotVersionsPage
function BotVersionsRoute() {
  const { botId } = useParams();
  return <BotVersionsPage botId={botId} />;
}

// AppContent component to handle location and conditional UI
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const isModelLabBuilderRoute =
    location.pathname === '/ai/ml/models/new' ||
    /^\/ai\/ml\/models\/[^/]+\/edit$/.test(location.pathname);
  const [isWatchlistModalOpen, setWatchlistModalOpen] = useState(false);
  const { items: watchlist, refresh: refreshWatchlist } = useWatchlistData();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isNotifOpen, setNotifOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  // cTrader modal state
  const [showCTraderModal, setShowCTraderModal] = useState(false);
  const [ctraderStatus, setCTraderStatus] = useState("starting");
  const [ctraderLoading, setCTraderLoading] = useState(false);
  const [ctraderError, setCTraderError] = useState(null);
  const [ctraderAccounts, setCTraderAccounts] = useState([]);
  const [ctraderInternalChoice] = useState("");
  const [ctraderTempId, setCTraderTempId] = useState("");

  // Determine if Sidebar, ThemeToggle, Chatbot, and PriceFeedProvider should be shown
  const showGlobalUI = !['/', '/login', '/register', '/verify-email', '/forgot-password', '/reset-password', '/admin-login', '/invest', '/invest/admin', '/invest/admin/economics'].includes(location.pathname);

  // Listen for service worker messages (notification clicks, auth token requests, approval actions)
  useEffect(() => {
    const handleSWMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.url) {
        navigate(event.data.url);
      }
      // SW requesting auth token for push action buttons
      if (event.data?.type === 'GET_AUTH_TOKEN' && event.ports?.[0]) {
        const token = localStorage.getItem('token');
        event.ports[0].postMessage({ token });
      }
      // Trade approval action completed from push button
      if (event.data?.type === 'TRADE_APPROVAL_ACTION') {
        const { action } = event.data;
        toast.success(action === 'approve' ? 'Trade approved from notification' : 'Trade rejected from notification');
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
  }, [navigate]);
  // Access shared trade context for trading-specific routes
  const { accountId: tradeAccountId, symbol: tradeSymbol, timeframe: tradeTimeframe } = useTrade();

  const isStudioRoute = location.pathname === '/studio';
  const isLiveTradeRoute = location.pathname === '/live-trade';

  // When a trading page becomes visible, fire a window resize so
  // lightweight-charts and react-grid-layout recalculate their dimensions.
  useEffect(() => {
    if (isStudioRoute || isLiveTradeRoute) {
      // Schedule after the browser paints (display:none -> visible needs a
      // frame for the layout engine to compute real dimensions).
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    }
  }, [isStudioRoute, isLiveTradeRoute]);

  // Reactive auth token state so auth-dependent UI updates without reload
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('token'));
  useEffect(() => {
    setAuthToken(localStorage.getItem('token'));
  }, [location.pathname]);
  useEffect(() => {
    const syncAuthToken = () => setAuthToken(localStorage.getItem('token'));
    const onStorage = (e) => {
      if (!e || e.key !== 'token') return;
      syncAuthToken();
    };
    window.addEventListener('valpha:login', syncAuthToken);
    window.addEventListener('valpha:logout', syncAuthToken);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('valpha:login', syncAuthToken);
      window.removeEventListener('valpha:logout', syncAuthToken);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const showProtectedUI = showGlobalUI && !!authToken;
  const showTradingWorkspace = showProtectedUI && (isStudioRoute || isLiveTradeRoute);

  // First-run onboarding: redirect newly-authenticated users to the wizard
  // when their profile has no `onboarding_completed_at`. Runs at most once
  // per session and only on entry routes (dashboard / root) to avoid
  // hijacking deep-links shared between users.
  useEffect(() => {
    if (!showProtectedUI) return;
    const ENTRY_PATHS = new Set(["/", "/dashboard"]);
    if (!ENTRY_PATHS.has(location.pathname)) return;
    let cancelled = false;
    const check = async () => {
      try {
        if (sessionStorage.getItem("valpha:onboarding-checked") === "1") return;
        sessionStorage.setItem("valpha:onboarding-checked", "1");
      } catch { /* private mode — best-effort */ }
      try {
        const { getUserProfile } = await import("./api/api");
        const profile = await getUserProfile();
        if (cancelled) return;
        if (profile && !profile.onboarding_completed_at) {
          navigate("/onboarding", { replace: true });
        }
      } catch {
        // network/profile error — don't block app on onboarding check
      }
    };
    check();
    return () => { cancelled = true; };
  }, [showProtectedUI, location.pathname, navigate]);

  // Reset onboarding-check sentinel on logout so the next login re-checks.
  useEffect(() => {
    const reset = () => {
      try { sessionStorage.removeItem("valpha:onboarding-checked"); } catch { /* ignore */ }
    };
    window.addEventListener("valpha:logout", reset);
    return () => window.removeEventListener("valpha:logout", reset);
  }, []);

  const guarded = (surface, element, fallbackTo) => (
    <AccessRouteGate surface={surface} fallbackTo={fallbackTo || surface?.fallbackTo}>
      {element}
    </AccessRouteGate>
  );

  const withIndicatorMetadata = (element) => (
    <IndicatorMetadataProvider>{element}</IndicatorMetadataProvider>
  );

  const withStrategyBuilderProviders = (element) => (
    <StrategySchemaProvider>
      <IndicatorMetadataProvider>{element}</IndicatorMetadataProvider>
    </StrategySchemaProvider>
  );

  // Fetch watchlist once per authenticated app session. Consumers reuse the
  // provider cache instead of issuing their own mount-time list calls.
  useEffect(() => {
    const fetchInitialWatchlist = async () => {
      try {
        console.log("App.jsx: Attempting to fetch initial watchlist...");
        const items = await refreshWatchlist();
        console.log("App.jsx: Successfully fetched initial watchlist, length:", Array.isArray(items) ? items.length : 0);
      } catch (error) {
        if (authToken) {
          console.error("App.jsx: Failed to fetch initial watchlist:", error?.message || error);
        } else {
          console.log("App.jsx: No token found, skipping initial watchlist fetch.");
        }
      }
    };

    if (authToken && showGlobalUI) {
      fetchInitialWatchlist();
    } else {
      if (showGlobalUI) {
        console.log("App.jsx: No token, skipping watchlist fetch.");
      }
    }
  }, [authToken, showGlobalUI, refreshWatchlist]);

  useEffect(() => {
    if (!authToken) {
      try {
        sessionStorage.removeItem(FIRST_ACCOUNT_SKIP_KEY);
      } catch {
        void 0;
      }
      return;
    }

    if (location.pathname !== "/dashboard") {
      return;
    }

    let cancelled = false;

    const routeFirstTimeUsersToAccounts = async () => {
      let shouldSkipOnboarding = false;
      try {
        shouldSkipOnboarding = sessionStorage.getItem(FIRST_ACCOUNT_SKIP_KEY) === "1";
      } catch {
        shouldSkipOnboarding = false;
      }

      if (shouldSkipOnboarding) {
        return;
      }

      try {
        const response = await fetchAccounts();
        if (cancelled) return;

        const userAccounts = Array.isArray(response) ? response : (response?.results || []);
        if (userAccounts.length === 0) {
          navigate("/accounts?onboarding=1", { replace: true });
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to check first-account onboarding state:", error);
        }
      }
    };

    routeFirstTimeUsersToAccounts();

    return () => {
      cancelled = true;
    };
  }, [authToken, location.pathname, navigate]);

  // Helper to fetch accounts and prep modal using a tempId
  const fetchCTraderAccounts = async (tempId) => {
    if (!tempId) return;
    setShowCTraderModal(true);
    setCTraderStatus("awaitingTempId");
    setCTraderError(null);
    setCTraderLoading(true);
    setCTraderTempId(String(tempId));
    try {
      const data = await listCTraderAccounts(tempId);
      const list = Array.isArray(data?.accounts) ? data.accounts : Array.isArray(data) ? data : [];
      setCTraderAccounts(list);
      setCTraderStatus("selecting");

  // (Removed broken internal-account tracking injection)
    } catch (e) {
      setCTraderError(e);
    } finally {
      setCTraderLoading(false);
    }
  };

  // Listen for popup messages to drive the modal flow
  useEffect(() => {
    const allowed = new Set(getCallbackAllowedOrigins());
    const onMessage = (event) => {
      try {
        if (!event || !event.data || !event.origin) return;
        // Only accept messages from allowed origins (typically same-origin)
        if (!allowed.has(event.origin)) return;
        const { type, tempId } = event.data || {};
        if (type === "CTRADER_AUTH_STARTED") {
          setShowCTraderModal(true);
          setCTraderStatus("awaitingPopup");
          setCTraderError(null);
        } else if (type === "CTRADER_AUTH_DONE" && tempId) {
          fetchCTraderAccounts(tempId);
        }
  } catch { void 0; }
    };
    window.addEventListener("message", onMessage);
    // Fallback: listen for storage writes from popup callback
    const onStorage = (ev) => {
      try {
        if (!ev) return;
        if (ev.key === "ctrader_callback_temp_id" && ev.newValue) {
          const tempId = ev.newValue;
          localStorage.removeItem("ctrader_callback_temp_id");
          fetchCTraderAccounts(tempId);
        }
      } catch { void 0; }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Fallback: if whole tab gets redirected back with temp_id in URL, still handle it
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tempId = params.get("ctrader_temp_id") || params.get("temp_id") || params.get("state");
    if (!tempId) return;
    fetchCTraderAccounts(tempId);
  }, [location.search]);

  const handleCTraderSelect = async (acc) => {
    try {
      const params = new URLSearchParams(location.search);
      const tempId = params.get("ctrader_temp_id") || params.get("temp_id") || params.get("state");
      const cookieMatch = (document.cookie || '').match(/(?:^|; )ctrader_pending_internal_account_id=([^;]+)/);
      const internalId =
        localStorage.getItem("ctrader_pending_internal_account_id") ||
        sessionStorage.getItem("ctrader_pending_internal_account_id") ||
        ctraderInternalChoice ||
        (cookieMatch ? decodeURIComponent(cookieMatch[1]) : "");
      if (!tempId || !internalId) {
        setCTraderError(new Error("Missing onboarding context. Please start connect from this tab."));
        return;
      }
      setCTraderStatus("completing");
      const ctraderAccountId = acc?.ctrader_account_id || acc?.ctidTraderAccountId || acc?.accountId || acc?.id;
      await ctraderOnboardComplete(internalId, { temp_id: tempId, ctrader_account_id: ctraderAccountId });
      setShowCTraderModal(false);
      localStorage.removeItem("ctrader_pending_internal_account_id");
  try { document.cookie = "ctrader_pending_internal_account_id=; Max-Age=0; Path=/; SameSite=Lax"; } catch { void 0; }
      navigate("/accounts");
    } catch (e) {
      setCTraderError(e);
      setCTraderStatus("selecting");
    }
  };

  const handleCTraderRetry = () => {
    if (!ctraderTempId) return;
    setCTraderError(null);
    setCTraderLoading(true);
    (async () => {
      try {
        const data = await listCTraderAccounts(ctraderTempId);
        const list = Array.isArray(data?.accounts) ? data.accounts : Array.isArray(data) ? data : [];
        setCTraderAccounts(list);
  setCTraderStatus("selecting");
      } catch (e) {
        setCTraderError(e);
      } finally {
        setCTraderLoading(false);
      }
    })();
  };

  const { backgroundVideo } = useTheme();
  const hasVideo = !!backgroundVideo?.url;

  const appBody = (
    <div className={`flex min-h-screen text-textPrimary ${hasVideo ? '' : 'bg-backgroundBody'} ${showGlobalUI ? "" : "bg-gradient-to-br from-[#0F1826] via-[#0F1826] to-[#0F1826]"}`}
      style={{ '--topbar-height': showProtectedUI ? '44px' : '0px' }}
    >
      <VideoBackground />
      <Toaster />
      <InstallAppBanner />
      {showProtectedUI && <UpgradeFeatureGateModal />}
      {showProtectedUI && (
        <>
          <Sidebar setWatchlistModalOpen={setWatchlistModalOpen} isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen((v) => !v)} onChatToggle={() => setIsChatOpen(prev => !prev)} isChatOpen={isChatOpen} />
        </>
      )}
      <div
        className={showProtectedUI ? `relative z-10 flex-1 min-w-0 overflow-x-hidden transition-all duration-300 ${isSidebarOpen ? 'sidebar-open' : 'sidebar-collapsed'}` : "relative z-10 flex-1"}
      >
        {showProtectedUI && <AgentChat isExternalOpen={isChatOpen} onExternalOpenChange={setIsChatOpen} hideFloatingButton />}
        {/* Global top navbar with notifications + AI Assistant */}
        {showProtectedUI && (
          <div className="flex items-center justify-end gap-2 px-3 py-1.5 border-b border-borderDefault bg-sidebar text-sidebarText"
            style={{ minHeight: 40 }}
          >
            <button
              onClick={() => setIsChatOpen(prev => !prev)}
              aria-label="AI Assistant"
              className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full transition ${
                isChatOpen ? 'bg-primary text-textAccent' : 'text-sidebarText hover:bg-primary/20'
              }`}
            >
              <Sparkles className="w-5 h-5" />
            </button>
            <NotificationsBadge onClick={() => setNotifOpen(true)} />
          </div>
        )}
        {showProtectedUI && <TrialBanner />}
        <div
          className={showProtectedUI ? (isModelLabBuilderRoute ? "p-0" : "p-0.5 sm:p-1 md:p-4 pt-1 md:pt-1") : ""}
          style={showProtectedUI && isModelLabBuilderRoute ? { height: 'calc(100vh - var(--topbar-height))', overflow: 'hidden' } : undefined}
        >
        {showProtectedUI ? (
          <>
            {showTradingWorkspace && (
              <PriceFeedProvider accountId={tradeAccountId} symbol={tradeSymbol} timeframe={tradeTimeframe}>
                {isStudioRoute && (
                  <AccessRouteGate surface={APP_ROUTE_SURFACES.studio} fallbackTo={APP_ROUTE_SURFACES.studio.fallbackTo}>
                  <div>
                    <NewStudioPage watchlist={watchlist} />
                  </div>
                  </AccessRouteGate>
                )}
                {isLiveTradeRoute && (
                  <AccessRouteGate surface={APP_ROUTE_SURFACES.liveTrade} fallbackTo={APP_ROUTE_SURFACES.liveTrade.fallbackTo}>
                  <div>
                    <LiveTradePage watchlist={watchlist} />
                  </div>
                  </AccessRouteGate>
                )}
              </PriceFeedProvider>
            )}
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Navigate to="/dashboard" replace />} />
              <Route path="/register" element={<Navigate to="/dashboard" replace />} />
              <Route path="/verify-email" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={guarded(APP_ROUTE_SURFACES.dashboard, <Dashboard watchlist={watchlist} />)} />
              <Route path="/accounts" element={guarded(APP_ROUTE_SURFACES.accounts, <AccountsHubPreviewPage />)} />
              <Route path="/accounts/legacy" element={guarded(APP_ROUTE_SURFACES.accountsLegacy, <Accounts />)} />
              <Route path="/accounts/preview" element={<Navigate to="/accounts" replace />} />
              <Route path="/accounts/:account_uuid/trade-history" element={guarded(APP_ROUTE_SURFACES.accountTradeHistory, <AccountTradeListPage />)} />
              <Route path="/trades/history/:trade_uuid" element={guarded(APP_ROUTE_SURFACES.tradeHistoryDetail, <TradeHistoryViewerPage />)} />
              <Route path="/select-account-for-history" element={guarded(APP_ROUTE_SURFACES.selectAccountForHistory, <SelectAccountForHistoryPage />)} />
              <Route path="/history" element={guarded(APP_ROUTE_SURFACES.selectAccountForHistory, <SelectAccountForHistoryPage />)} />
              <Route path="/settings" element={guarded(APP_ROUTE_SURFACES.settings, <SettingsPage />)} />
              <Route path="/settings/chart-snapshot-configs" element={guarded(APP_ROUTE_SURFACES.chartSnapshotConfigs, <ChartSnapshotConfigsPage />)} />
              <Route path="/settings/theme" element={guarded(APP_ROUTE_SURFACES.themeSettings, <ThemeSettingsPage />)} />
              <Route path="/settings/notifications" element={guarded(APP_ROUTE_SURFACES.notificationSettings, <NotificationSettingsPage />)} />
              <Route path="/analysis" element={guarded(APP_ROUTE_SURFACES.analysis, <HistoricalAnalysisPage />)} />
              <Route path="/analysis/:jobId" element={guarded(APP_ROUTE_SURFACES.analysisJobDetail, <AnalysisJobDetailsPage />)} />
              <Route path="/win-rate" element={guarded(APP_ROUTE_SURFACES.winRate, <WinRateCalculatorPage />)} />
              <Route path="/select-ctrader-account" element={guarded(APP_ROUTE_SURFACES.selectCTraderAccount, <SelectCTraderAccount />)} />
              {/* In popup, this route just posts tempId back and closes */}
              <Route path="/ctrader/callback" element={<CTraderPopupCallback />} />

              {/* Bots Routes */}
              <Route path="/bots" element={guarded(APP_ROUTE_SURFACES.botsList, <BotsListPage />)} />
              <Route path="/bots/new" element={guarded(APP_ROUTE_SURFACES.botCreate, <CreateBotPage />)} />
              <Route path="/bots/:botId" element={guarded(APP_ROUTE_SURFACES.botDetail, <BotDetailPage />)} />
              <Route path="/bots/:botId/versions" element={guarded(APP_ROUTE_SURFACES.botVersions, <BotVersionsRoute />)} />
              <Route path="/bots/:botId/versions/new" element={guarded(APP_ROUTE_SURFACES.botVersionCreate, withIndicatorMetadata(<CreateBotVersionPage />))} />
              <Route path="/bots/:botId/versions/:versionId" element={guarded(APP_ROUTE_SURFACES.botVersionDetail, <BotVersionDetailPage />)} />
              <Route path="/bots/:botId/versions/:versionId/backtest-configs/new" element={guarded(APP_ROUTE_SURFACES.backtestConfigCreate, <CreateEditBacktestConfigPage />)} />
              <Route path="/bots/:botId/versions/:versionId/backtest-configs/:configId" element={guarded(APP_ROUTE_SURFACES.backtestConfigEdit, <CreateEditBacktestConfigPage />)} />
              <Route path="/bots/:botId/strategy-builder" element={guarded(APP_ROUTE_SURFACES.strategyBuilder, withStrategyBuilderProviders(<StrategyBuilderPage />))} />
              <Route path="/bots/:botId/strategy-builder/:versionId" element={guarded(APP_ROUTE_SURFACES.strategyBuilder, withStrategyBuilderProviders(<StrategyBuilderPage />))} />
              <Route path="/bots/:botId/strategy-builder-v2" element={guarded(APP_ROUTE_SURFACES.strategyBuilderV2, <StrategyBuilderV2Page />)} />
              <Route path="/bots/:botId/strategy-builder-v2/:versionId" element={guarded(APP_ROUTE_SURFACES.strategyBuilderV2, <StrategyBuilderV2Page />)} />
              <Route path="/bots/:botId/live-runs" element={guarded(APP_ROUTE_SURFACES.liveRuns, <LiveRunsPage />)} />
              <Route path="/backtest-runs/:runId" element={guarded(APP_ROUTE_SURFACES.backtestRunDetail, withIndicatorMetadata(<BacktestRunDetailPage />))} />
              {/* Keep-alive routes: rendered persistently outside Routes; null here prevents catch-all redirect */}
              <Route path="/studio" element={null} />
              <Route path="/live-trade" element={null} />
              <Route path="/grid-test" element={<GridTestPage />} />
              <Route path="/chart-test" element={<ChartTestPage />} />
              <Route path="/monitoring" element={guarded(APP_ROUTE_SURFACES.monitoring, <MonitoringPage />)} />
              <Route path="/portfolio" element={guarded(APP_ROUTE_SURFACES.portfolio, <PortfolioPage />)} />
              <Route path="/copy-trading" element={guarded(APP_ROUTE_SURFACES.copyTrading, <CopyTradingPage />)} />
              {/* Trade Approvals */}
              <Route path="/approvals" element={guarded(APP_ROUTE_SURFACES.approvals, <PendingTradesPage />)} />
              <Route path="/approvals/:id" element={guarded(APP_ROUTE_SURFACES.approvalDetail, <TradeApprovalDetailPage />)} />
              {/* Alerts */}
              <Route path="/alerts" element={guarded(APP_ROUTE_SURFACES.alerts, withIndicatorMetadata(<AlertsListPage />))} />
              <Route path="/alerts/:id" element={guarded(APP_ROUTE_SURFACES.alertDetail, withIndicatorMetadata(<AlertDetailPage />))} />
              <Route path="/research" element={guarded(APP_ROUTE_SURFACES.researchHub, <ResearchHubPage />)} />
              <Route path="/tutorial" element={guarded(APP_ROUTE_SURFACES.tutorial, <TutorialPage />)} />
              <Route path="/fundamentals/economic-calendar" element={guarded(APP_ROUTE_SURFACES.economicCalendar, <EconomicCalendarPage />)} />
              <Route path="/fundamentals/news" element={guarded(APP_ROUTE_SURFACES.news, <NewsPage />)} />
              <Route path="/fundamentals/cot-report" element={guarded(APP_ROUTE_SURFACES.cotReport, <COTReportPage />)} />
              <Route path="/fundamentals/economic-indicators" element={guarded(APP_ROUTE_SURFACES.economicIndicators, <EconomicIndicatorsPage />)} />
              <Route path="/fundamentals/options" element={guarded(APP_ROUTE_SURFACES.options, <OptionsPage />)} />
              <Route path="/fundamentals/knowledge-graph" element={guarded(APP_ROUTE_SURFACES.knowledgeGraph, <KnowledgeGraphPage />)} />
              <Route path="/fundamentals/insider-trading" element={guarded(APP_ROUTE_SURFACES.insiderTrading, <InsiderTradingPage />)} />
              <Route path="/profile" element={guarded(APP_ROUTE_SURFACES.profile, <UserProfilePage />)} />
              <Route path="/subscription" element={guarded(APP_ROUTE_SURFACES.subscription, <SubscriptionPage />)} />
              <Route path="/billing" element={guarded(APP_ROUTE_SURFACES.billing, <BillingPage />)} />
              <Route path="/onboarding" element={guarded(APP_ROUTE_SURFACES.onboarding, <OnboardingPage />)} />
              <Route path="/platform-admin" element={guarded(APP_ROUTE_SURFACES.platformAdmin, <AdminSettingsPage />)} />

              {/* Data Warehouse */}
              <Route path="/market" element={guarded(APP_ROUTE_SURFACES.market, <MarketExplorerPage />)} />
              <Route path="/screener" element={guarded(APP_ROUTE_SURFACES.screener, <ScreenerPage />)} />
              <Route path="/explorer/:symbol" element={guarded(APP_ROUTE_SURFACES.explorer, <ExplorerPage />)} />

              {/* AI Platform routes (mounted under /ai/*) */}
              <Route path="/ai/*" element={guarded(APP_ROUTE_SURFACES.ai, <AIRoutes />)} />

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </>
        ) : (
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin-login" element={<AdminImpersonationLogin />} />
            <Route path="/invest" element={<InvestorPitchPage />} />
            <Route path="/invest/admin" element={<PitchDeckAdminPage />} />
            <Route path="/invest/admin/economics" element={<UnitEconomicsAdminPage />} />
            <Route path="*" element={<Navigate to="/" />} />
            <Route path="/login" element={authToken ? <Navigate to="/dashboard" replace /> : <Login />} />
            <Route path="/register" element={authToken ? <Navigate to="/dashboard" replace /> : <Register />} />
            <Route path="/verify-email" element={authToken ? <Navigate to="/dashboard" replace /> : <VerifyEmail />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        )}
        </div>
      </div>
      {showProtectedUI && isWatchlistModalOpen && (
        <WatchlistModal
          setWatchlistModalOpen={setWatchlistModalOpen}
        />
      )}
      {/* cTrader onboarding modal overlay */}
      <CTraderOnboardingModal
        open={showCTraderModal}
        onClose={() => setShowCTraderModal(false)}
        loading={ctraderLoading}
        error={ctraderError}
        accounts={ctraderAccounts}
        onSelect={handleCTraderSelect}
        onRetry={handleCTraderRetry}
        status={ctraderStatus}
      />
  {/* Notifications drawer */}
  {showProtectedUI && (
    <NotificationsDrawer open={isNotifOpen} onClose={() => setNotifOpen(false)} />
  )}
  {/* Theme toggle moved into Sidebar */}
    </div>
  );

  // Wrap the entire global, authenticated UI with NotificationsProvider so all consumers (Badge/Drawer) get context
  if (showProtectedUI) {
    return <NotificationsProvider>{appBody}</NotificationsProvider>;
  }
  return appBody;
}

// Main App component wraps AppContent with Router
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
